// public-site/src/screens/Balance.js
// Loads and shows the case balance. "Pay now" continues to the payment screen.

import React, { useEffect, useState } from 'react';
import { getCase, formatUSD } from '../api';
import { styles, colors, SUPPORT_PHONE } from '../theme';
import Brand from '../components/Brand';

// "2026-07-01" → "July 1, 2026" (parsed as local date to avoid TZ drift).
function formatDueDate(dateStr) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateStr || ''));
  if (!m) return dateStr;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function formatCardLabel(card) {
  const brand = (card.cardType || 'card').charAt(0).toUpperCase() + (card.cardType || 'card').slice(1);
  const last4 = card.cardLastFour ? ` ••••${card.cardLastFour}` : '';
  const exp = card.cardExpiration ? ` (exp ${card.cardExpiration})` : '';
  return `${brand}${last4}${exp}`;
}

export default function Balance({ caseInfo, onCaseLoaded, onPay, onSignOut }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const result = await getCase();
        if (!active) return;
        setData(result);
        if (onCaseLoaded) onCaseLoaded(result);
      } catch (err) {
        if (!active) return;
        if (err.status === 401 || err.status === 403) {
          onSignOut();
          return;
        }
        setError(err.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [onCaseLoaded, onSignOut]);

  const decedentName = data?.decedentName || caseInfo?.decedentName || '';
  const caseNumber = data?.caseNumber || caseInfo?.caseNumber || '';

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <Brand tagline="Account balance" />

        {loading && <p style={styles.bodyText}>Loading your balance…</p>}

        {!loading && error && (
          <>
            <div style={styles.errorBox}>{error}</div>
            <button style={styles.buttonSecondary} onClick={onSignOut}>
              Back to sign in
            </button>
          </>
        )}

        {!loading && !error && data && (
          <>
            <h1 style={styles.title}>In memory of {decedentName}</h1>
            <p style={{ ...styles.bodyText, ...{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '16px', color: colors.textMuted } }}>
              Case {caseNumber}
            </p>

            <div
              style={{
                background: colors.nested,
                border: `1px solid ${colors.hairline}`,
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center',
                margin: '8px 0 24px',
              }}
            >
              <div style={{ ...styles.label, marginBottom: '6px' }}>Amount due</div>
              <div style={{ fontFamily: "'EB Garamond', Georgia, serif", fontSize: '44px', fontWeight: 500, color: data.balance > 0 ? colors.white : colors.gold }}>
                {formatUSD(data.balance)}
              </div>
            </div>

            {data.plan && data.plan.status === 'Active' && (
              <div
                style={{
                  background: colors.nested,
                  border: `1px solid ${colors.hairline}`,
                  borderRadius: '12px',
                  padding: '16px 18px',
                  margin: '0 0 18px',
                }}
              >
                <div style={{ ...styles.label, marginBottom: '6px' }}>Payment plan</div>
                <div style={{ ...styles.bodyText, margin: 0, color: colors.textPrimary }}>
                  {formatUSD(data.plan.installmentAmount)} {String(data.plan.frequency || '').toLowerCase()}
                  {data.plan.totalPayments ? ` · ${data.plan.paymentsMade || 0} of ${data.plan.totalPayments} paid` : ''}
                </div>
                {data.plan.nextDueDate && (
                  <div style={{ ...styles.bodyText, margin: '4px 0 0', fontSize: '16px', color: colors.textMuted }}>
                    Next payment due {formatDueDate(data.plan.nextDueDate)}
                  </div>
                )}
              </div>
            )}

            {data.cardOnFile && data.cardOnFile.hasVault && (
              <p style={{ ...styles.bodyText, fontSize: '16px', color: colors.textMuted, margin: '0 0 18px' }}>
                Card on file: {formatCardLabel(data.cardOnFile)}
              </p>
            )}

            {/* Recurring transparency — only while a balance remains so nothing
                implies a future charge once the account is paid off. */}
            {data.plan && data.plan.status === 'Active' && data.balance > 0 && (
              data.cardOnFile && data.cardOnFile.hasVault ? (
                <p style={{ ...styles.bodyText, fontSize: '16px', color: colors.textSecondary, margin: '0 0 18px', lineHeight: 1.5 }}>
                  Automatic payments are on — we'll charge your card ending ••{data.cardOnFile.cardLastFour}{' '}
                  {formatUSD(data.plan.installmentAmount)} {String(data.plan.frequency || '').toLowerCase()} on each due
                  date until paid. Call {SUPPORT_PHONE} to make changes.
                </p>
              ) : (
                <p style={{ ...styles.bodyText, fontSize: '16px', color: colors.textSecondary, margin: '0 0 18px', lineHeight: 1.5 }}>
                  Pay each installment here, or save a card on the next screen to turn on automatic payments.
                </p>
              )
            )}

            {data.payable ? (
              <button style={styles.buttonPrimary} onClick={onPay}>
                {data.plan && data.plan.status === 'Active' ? 'Pay this installment' : 'Pay now'}
              </button>
            ) : (
              <p style={{ ...styles.bodyText, textAlign: 'center' }}>
                {data.status === 'Closed'
                  ? 'This account is closed. Please contact the funeral home with any questions.'
                  : 'There is no balance due at this time. Thank you.'}
              </p>
            )}

            <button
              style={{ ...styles.buttonSecondary, marginTop: '14px', border: 'none', color: colors.textMuted }}
              onClick={onSignOut}
            >
              Sign out
            </button>
          </>
        )}
      </div>
    </div>
  );
}
